"use client";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { Messages2, Send2, Trash } from "iconsax-react";
import { formatNumberWithSuffix } from "@/lib/formatters";
import { useState, useEffect, useCallback } from "react";
import { SupportedLanguage } from "@/lib/shared";
import lang from "@/app/[locale]/(main)/lang.json";
import {
  getVideoCommentsByVideoId,
  commentVideoById,
  deleteCommentVideoById,
} from "@/actions/videos";
import { TVideoComment } from "@/actions/types";
import { Link } from "react-router-dom";
import { localizedPath } from "@/lib/localePath";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";

type CommentsSheetProps = {
  comments: number;
  videoId: string;
  isLoggedIn: boolean;
  onCommentCountChange?: (newCount: number) => void;
  locale?: SupportedLanguage;
  /** لون أيقونة الزر في شريط الريل (موبايل)، مثل `text-black` */
  triggerIconClassName?: string;
  /** موبايل — أيقونة مفرغة وخلفية شفافة مع حدود فاتحة */
  reelRailMode?: boolean;
};

export default function CommentsSheet({
  comments,
  videoId,
  isLoggedIn,
  onCommentCountChange,
  locale = "en",
  triggerIconClassName,
  reelRailMode = false,
}: CommentsSheetProps) {
  const [videoComments, videoCommentsSet] = useState<TVideoComment[]>([]);
  const [isLoading, isLoadingSet] = useState(false);
  const [newComment, newCommentSet] = useState("");
  const [isSubmitting, isSubmittingSet] = useState(false);
  const [isOpen, isOpenSet] = useState(false);

  const loadComments = useCallback(async () => {
    if (!videoId) return;

    isLoadingSet(true);
    try {
      const result = await getVideoCommentsByVideoId(videoId, {
        pageNumber: 1,
        pageSize: 50,
      });

      if (result.status === "success" && result.metaData) {
        videoCommentsSet(result.metaData);
      }
    } catch (error) {
      console.error("Failed to load comments:", error);
    } finally {
      isLoadingSet(false);
    }
  }, [videoId]);

  const handleSubmitComment = async () => {
    if (!newComment.trim() || !videoId || isSubmitting || !isLoggedIn) return;

    isSubmittingSet(true);
    try {
      const result = await commentVideoById(videoId, {
        message: newComment.trim(),
      });

      if (result.status === "success") {
        newCommentSet("");
        // Reload comments to show the new one
        await loadComments();
        // Update parent component with new count
        onCommentCountChange?.(comments + 1);
      }
    } catch (error) {
      console.error("Failed to submit comment:", error);
    } finally {
      isSubmittingSet(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmitComment();
    }
  };

  // Load comments when sheet opens
  useEffect(() => {
    if (isOpen) {
      loadComments();
    }
  }, [isOpen, videoId, loadComments]);

  return (
    <Sheet open={isOpen} onOpenChange={isOpenSet}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="flex flex-col cursor-pointer items-center justify-center gap-1"
        >
          <span
            className={cn(
              "rounded-full p-2",
              reelRailMode ? "" : "bg-gray-200",
            )}
          >
            <Messages2
              size={24}
              className={cn("fill-current", triggerIconClassName)}
              variant={
                reelRailMode ? "Outline" : comments > 0 ? "Bold" : "Outline"
              }
            />
          </span>
          <span
            className={cn(
              comments > 0 ? "text-sm" : "text-xs",
              reelRailMode && "text-white",
            )}
          >
            {comments > 0
              ? formatNumberWithSuffix(comments)
              : lang["action-comment"][locale]}
          </span>
        </button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Comments</SheetTitle>
          <SheetDescription>
            {formatNumberWithSuffix(comments)} comments on this video
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col h-[calc(100vh-100px)]">
          {/* Comments list with overflow */}
          <div className="flex-1 overflow-auto max-h-[calc(100vh-200px)] pr-2">
            <div className="space-y-4 pb-4 px-4">
              {videoComments.length > 0 ? (
                videoComments.map((comment) => {
                  return (
                    <div key={comment.id} className="flex items-center">
                      <div className="flex flex-col gap-y-1">
                        <Link
                          to={localizedPath(locale, `/profiles/${comment.senderId}`)}
                          className="text-xs hover:underline text-gray-500 font-semibold"
                        >
                          {comment.senderName}
                        </Link>

                        <p className="text-sm">{comment.message}</p>
                        <span className="text-[10px] text-gray-600">
                          {formatDistanceToNow(comment.sentOn)}
                        </span>
                      </div>

                      <span className="ml-auto"></span>

                      {!comment.isOwner && (
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          onClick={async () => {
                            const result = await deleteCommentVideoById(comment.id);
                            if (result.status === "success") {
                              await loadComments();
                              // Update parent component with new count
                              onCommentCountChange?.(Math.max(0, comments - 1));
                            }
                          }}
                        >
                          <Trash
                            size={12}
                            variant="Outline"
                            className="fill-red-500"
                          />
                        </Button>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="flex items-center justify-center py-8">
                  <p className="text-muted-foreground">
                    No comments yet. Be the first to comment!
                  </p>
                </div>
              )}
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <p className="text-muted-foreground">Loading comments...</p>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Sticky comment input section */}
        <div className="sticky bottom-0 bg-background border-t border-border py-4">
          {isLoggedIn ? (
            <div className="flex gap-2 pr-4">
              <textarea
                placeholder="Add a comment..."
                value={newComment}
                onChange={(e) => newCommentSet(e.target.value)}
                onKeyDown={handleKeyPress}
                disabled={isSubmitting}
                className="focus-within:outline-none focus-within:border-none focus-within:ring-0 focus-within:ring-offset-0 focus-within:ring-offset-background focus-within:ring-transparent text-sm px-4 py-2 flex-1 resize-none"
              />

              <Button
                onClick={handleSubmitComment}
                disabled={!newComment.trim() || isSubmitting}
                size="icon"
              >
                <Send2 size={16} className="fill-current" variant="Outline" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center py-4">
              <Link
                to={localizedPath(locale, "/auth/login")}
                className="text-muted-foreground text-sm"
              >
                Please log in to add a comment
              </Link>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
